import { useEffect, useState } from 'react';
import { Modal, TextInput, Button, InlineLoading, InlineNotification } from '@carbon/react';
import { MagicWand } from '@carbon/icons-react';

export interface RenameDocumentDialogProps {
  isOpen: boolean;
  /** Current label shown (display_name || filename). */
  currentName: string;
  /** The original filename, shown as a hint (frozen; only the label changes). */
  filename: string;
  onSave: (displayName: string) => void | Promise<void>;
  onClose: () => void;
  isSaving?: boolean;
  /**
   * Whether the "Rename with AI" action can be offered for this document
   * (fully processed + an AI provider configured). Omitting this hides the
   * button entirely.
   */
  canSuggest?: boolean;
  /**
   * Fetch an AI-suggested display name from the document's own knowledge
   * units. Resolves to the suggested string; the dialog fills the input with
   * it but never auto-saves — the user still reviews/edits and clicks Save.
   */
  onSuggestName?: () => Promise<string>;
}

const MAX = 200;

/**
 * RenameDocumentDialog — set a document's user-facing label (Feedback A15).
 * The file itself (COS object / filename) is never moved; this only changes the
 * display label. Empty input is allowed and clears the label (falls back to the
 * filename), matching the backend PATCH semantics.
 *
 * "Rename with AI": when `canSuggest` + `onSuggestName` are provided, a button
 * fills the input with a suggested name derived from the document's own
 * knowledge units — the user still reviews/edits before clicking Save.
 */
export const RenameDocumentDialog = ({
  isOpen,
  currentName,
  filename,
  onSave,
  onClose,
  isSaving = false,
  canSuggest = false,
  onSuggestName,
}: RenameDocumentDialogProps) => {
  const [name, setName] = useState(currentName);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setName(currentName);
      setSuggestError(null);
    }
  }, [isOpen, currentName]);

  const tooLong = name.trim().length > MAX;

  const handleSubmit = () => {
    if (tooLong) return;
    void onSave(name.trim());
  };

  const handleSuggest = async () => {
    if (!onSuggestName) return;
    setIsSuggesting(true);
    setSuggestError(null);
    try {
      const suggested = await onSuggestName();
      setName(suggested);
    } catch {
      setSuggestError('Could not generate a suggestion. You can still rename manually.');
    } finally {
      setIsSuggesting(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      modalHeading="Rename document"
      primaryButtonText={isSaving ? 'Saving…' : 'Save'}
      secondaryButtonText="Cancel"
      primaryButtonDisabled={tooLong || isSaving}
      onRequestClose={onClose}
      onRequestSubmit={handleSubmit}
      size="sm"
    >
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', marginBottom: '0.5rem' }}>
        <div style={{ flex: 1 }}>
          <TextInput
            id="rename-document-name"
            data-testid="rename-document-name"
            labelText="Display name"
            helperText={`Original file: ${filename} (unchanged). Leave empty to show the filename.`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            invalid={tooLong}
            invalidText={`Name must be at most ${MAX} characters`}
            maxLength={MAX + 1}
          />
        </div>
        {canSuggest && onSuggestName && (
          <Button
            kind="tertiary"
            size="md"
            renderIcon={isSuggesting ? undefined : MagicWand}
            disabled={isSuggesting || isSaving}
            onClick={handleSuggest}
            data-testid="rename-document-suggest"
            style={{ whiteSpace: 'nowrap' }}
          >
            {isSuggesting ? <InlineLoading description="Suggesting…" /> : 'Rename with AI'}
          </Button>
        )}
      </div>
      {suggestError && (
        <InlineNotification
          kind="warning"
          title=""
          subtitle={suggestError}
          lowContrast
          hideCloseButton
        />
      )}
    </Modal>
  );
};
